/**
 * Pure routing decisions for the router guard (router/index.js): which role may open a
 * route, where each role lands, which login a signed-out visitor is sent to, and whether a
 * `?redirect=` target is safe to follow.
 *
 * Kept free of localStorage, Vue and the router so tests/unit/route-access.test.mjs can run
 * it under `node --test`. The mobile app's equivalents are vue/jobie-mobile/src/utils/
 * roleHelper.js and validators.js `safeRedirect`.
 *
 * Roles arrive as the backend names them (JcSeeker, JcEmployer, JcPanelist) or in the
 * short legacy form (Seeker, Employer, Panelist), in any case.
 */

const SEEKER_ROLES = ["seeker", "jcseeker"];
const EMPLOYER_ROLES = ["employer", "jcemployer"];
const PANELIST_ROLES = ["panelist", "jcpanelist"];

/** Where a panelist lands, and where the guard sends them from a page outside their whitelist. */
export const PANELIST_HOME = "/employer/interviews";

// Paths a panelist may reach on the employer tree. Anything outside this list redirects to
// PANELIST_HOME. Keep this narrow — the list of allowed pages IS the panelist portal until a
// proper /panelist/* tree ships. Matching is by prefix, so "/employer/applications/" (with the
// slash) admits /employer/applications/:id/interviews while /employer/applications stays blocked.
export const PANELIST_ALLOWED_PATHS = [
  "/employer/interviews",
  "/employer/interviews/",           // trailing slash + /:interviewId/feedback
  "/employer/applications/",         // /employer/applications/:id/interviews (round-scoped view)
  "/employer/change-password",
  "/employer/profile",
  "/employer/view-profile",
  "/panelist/",                      // panelist-portal sub-tree (earnings, etc.)
];

/** Lower-cased, trimmed role name; "" for anything that is not a non-empty string. */
export function normalizeRole(role) {
  return typeof role === "string" ? role.trim().toLowerCase() : "";
}

/** The role carried by a stored `userdata` object: `role`, else the first of `roles`. */
export function roleOf(userdata) {
  if (!userdata || typeof userdata !== "object") return "";
  if (typeof userdata.role === "string" && userdata.role) return userdata.role;
  if (Array.isArray(userdata.roles) && userdata.roles.length > 0) return userdata.roles[0] || "";
  return "";
}

export function isSeekerRole(role) {
  return SEEKER_ROLES.includes(normalizeRole(role));
}

/** An employer proper. Panelists share the /employer/* tree but are not employers. */
export function isEmployerRole(role) {
  return EMPLOYER_ROLES.includes(normalizeRole(role));
}

export function isPanelistRole(role) {
  return PANELIST_ROLES.includes(normalizeRole(role));
}

export function isPathAllowedForPanelist(path) {
  if (typeof path !== "string") return false;
  return PANELIST_ALLOWED_PATHS.some((p) => path === p || path.startsWith(p));
}

/** The dashboard a role lands on; "/login" when the role is unknown. */
export function getDashboardRoute(role) {
  if (isEmployerRole(role)) return "/employer/dashboard";
  if (isSeekerRole(role)) return "/seeker/dashboard";
  // Panelists land straight on their assigned-interviews list — they don't own the employer
  // dashboard tiles (jobs/wallet/etc.).
  if (isPanelistRole(role)) return PANELIST_HOME;
  return "/login";
}

/**
 * The login that matches a role. Used on logout and 401 so an employer is not bounced to the
 * seeker login.
 */
export function getLoginRoute(role) {
  return isEmployerRole(role) || isPanelistRole(role) ? "/employer/login" : "/seeker/login";
}

/** The login for a signed-out visitor, chosen by the page they tried to open. */
export function getLoginRouteForPath(path) {
  if (typeof path === "string" && (path.startsWith("/employer/") || path.startsWith("/panelist/"))) {
    return "/employer/login";
  }
  return "/seeker/login";
}

/**
 * Whether a signed-in `role` may open `path`, whose route declares `requiredRole`
 * (meta.role: "Seeker", "Employer" or none). Seeker routes need a seeker; Employer routes
 * need an employer or a panelist; a panelist is further held to PANELIST_ALLOWED_PATHS on
 * every route.
 */
export function canAccessRoute(role, requiredRole, path) {
  if (isPanelistRole(role) && !isPathAllowedForPanelist(path)) return false;
  if (requiredRole === "Seeker") return isSeekerRole(role);
  if (requiredRole === "Employer") return isEmployerRole(role) || isPanelistRole(role);
  return true;
}

/**
 * `target` when it is a same-origin path the app may navigate to after login, else null:
 * it must start with "/", not "//", and contain no "://" or backslash (browsers read "/\" as "//").
 */
export function safeRedirect(target) {
  if (typeof target !== "string") return null;
  if (!target.startsWith("/") || target.startsWith("//")) return null;
  if (target.includes("://") || target.includes("\\")) return null;
  return target;
}
